import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { Me, OperatorPublic } from "@ftpedi/contracts";
import { fetchMe, meQueryKey } from "./lib/session.tsx";
import { AppShell } from "./components/app/app-shell.tsx";
import { adminRoutes } from "./areas/admin/routes.tsx";
import { portalRoutes } from "./areas/portal/routes.tsx";
import { platformRoutes } from "./areas/platform/routes.tsx";
import { NotFoundPage } from "./components/app/not-found.tsx";
import { RouteErrorPage } from "./components/app/route-error.tsx";

export interface RouterContext {
  queryClient: QueryClient;
  operator: OperatorPublic;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorPage,
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: lazyRouteComponent(() => import("./areas/public/login.tsx")),
  validateSearch: (s: Record<string, unknown>) => ({ next: typeof s["next"] === "string" ? s["next"] : undefined }),
});
export const welcomeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/welcome", component: lazyRouteComponent(() => import("./areas/public/welcome.tsx")) });
export const enquireRoute = createRoute({ getParentRoute: () => rootRoute, path: "/enquire", component: lazyRouteComponent(() => import("./areas/public/enquire.tsx")) });
export const styleguideRoute = createRoute({ getParentRoute: () => rootRoute, path: "/styleguide", component: lazyRouteComponent(() => import("./areas/public/styleguide.tsx")) });
export const bootstrapRoute = createRoute({ getParentRoute: () => rootRoute, path: "/platform-setup", component: lazyRouteComponent(() => import("./areas/platform/bootstrap.tsx")) });

async function requireSession(ctx: RouterContext, href: string): Promise<Me> {
  const me = await ctx.queryClient.ensureQueryData({ queryKey: meQueryKey, queryFn: fetchMe });
  if (!me) throw redirect({ to: "/login", search: { next: href } });
  return me;
}

export const appRoute = createRoute({
  id: "app",
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ context, location }) => {
    const me = await requireSession(context, location.href);
    if (me.mustChangePassword && !location.pathname.startsWith("/account")) throw redirect({ to: "/account", search: { tab: "security", forced: "1" } as never });
    return { me };
  },
  component: AppShell,
});

export function createAppRouter(queryClient: QueryClient, operator: OperatorPublic) {
  const areaChildren = operator.surface === "portal" ? portalRoutes(appRoute) : [...adminRoutes(appRoute), ...platformRoutes(appRoute)];
  const routeTree = rootRoute.addChildren([loginRoute, welcomeRoute, enquireRoute, styleguideRoute, bootstrapRoute, appRoute.addChildren(areaChildren)]);
  return createRouter({
    routeTree,
    context: { queryClient, operator },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
